import React from 'react';
import { makeStyles } from '@material-ui/core';
import Resizer from 'react-image-file-resizer';

import CapturerProps from './CapturerProps';

import { localFormat, maxImageSize, maxQuality } from './CapturerPreferences';

const useStyles = makeStyles({
  input: {
    display: 'none',
  },
});

const LocalPhotoCapturer = ({
  onImageCaptured,
  imageIdentifier,
  onCaptureCancelled,
}: CapturerProps): JSX.Element => {
  /** Note: leave the `React` namespace here, as jest.spyOn relies on this */
  const inputReference = React.useRef<HTMLInputElement>(null);
  const classes = useStyles();

  /**
   * We trigger the click as soon as the component mounts, so that the file picker appears just as when
   * clicking a file input's "upload" button.
   *
   * This is required since the user doesn't interact directly with the file input, rather, we route this
   * execution through a transformation and resizing logic before reporting it to the usePhotoCapturer
   * hook.
   *
   * Note: leave the `React` namespace here, as jest.spyOn relies on this
   */
  React.useEffect(() => {
    if (inputReference.current) {
      inputReference.current.click();
    }
  }, [inputReference]);

  /**
   * This method will just call imageFileResizer with the required parameters, taken from
   * CapturerPreferences to match the parameters used by PhotoCapturer.
   *
   * @param image - The image file to resize and convert
   */
  function processImage(image: File): void {
    Resizer.imageFileResizer(
      image,
      maxImageSize,
      maxImageSize,
      localFormat,
      maxQuality,
      0, // <-- This is the rotation, we are not using it
      (uri): void => onImageCaptured(imageIdentifier, uri as string),
      'base64',
    );
  }

  /**
   * Returns true if the mime type is valid. This is mostly a sanity check, since ideally the
   * input should not allow the user to add files with other mime types.
   *
   * @param fileType - The type detected on the file instance
   */
  function isValidFile(fileType: string): boolean {
    return ['image/png', 'image/jpeg', 'image/gif'].includes(fileType);
  }

  /**
   * Called when a new file is defined for the input.
   *
   * @param e - The input[type="file"]'s event
   */
  function handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
    if (e.target.files && e.target.files.length > 0) {
      const firstFile = e.target.files[0];
      if (isValidFile(firstFile.type)) {
        processImage(firstFile);
      }
    } else {
      onCaptureCancelled();
    }
  }

  return (
    <input
      ref={inputReference}
      type="file"
      data-testid="file-upload"
      className={classes.input}
      onChange={handleChange}
      accept="image/*"
    />
  );
};

export default LocalPhotoCapturer;
